import { buildExerciseSplitRanges, buildLapRanges } from './activityKmLapLink';

export function isWorkIntensitySplit(s) {
  const ic = (s.run_intensity_class || 'work').toLowerCase();
  return ic !== 'warmup' && ic !== 'cooldown' && ic !== 'rest';
}

/**
 * Show work-phase averages when the plan mixes work with other phases, or has multiple work segments.
 */
export function shouldShowWorkSplitSummary(exerciseSplits) {
  if (!Array.isArray(exerciseSplits) || exerciseSplits.length === 0) return false;
  const workCount = exerciseSplits.filter(isWorkIntensitySplit).length;
  const nonWorkCount = exerciseSplits.length - workCount;
  return workCount >= 2 || (workCount >= 1 && nonWorkCount >= 1);
}

function lapMovingTimeSec(lap) {
  const m = parseInt(lap.moving_time, 10);
  if (Number.isFinite(m) && m > 0) return m;
  const e = parseInt(lap.elapsed_time, 10);
  return Number.isFinite(e) && e > 0 ? e : 0;
}

/** Planned work segment durations (sec), e.g. 7×420 for 7 min reps */
function collectWorkDurationTargets(exerciseSplits) {
  const targets = [];
  for (const s of exerciseSplits) {
    if (!isWorkIntensitySplit(s)) continue;
    const d = Number(s.duration_sec) || 0;
    if (d >= 45) targets.push(d);
  }
  return targets;
}

function collectRestDurationTargets(exerciseSplits) {
  const targets = [];
  for (const s of exerciseSplits) {
    if (isWorkIntensitySplit(s)) continue;
    const d = Number(s.duration_sec) || 0;
    if (d >= 15 && d < 720) targets.push(d);
  }
  return targets;
}

function matchesDurationTarget(lapSec, targetSec) {
  const tol = Math.max(22, targetSec * 0.12);
  return Math.abs(lapSec - targetSec) <= tol;
}

/**
 * Strava laps whose moving time matches a planned work duration (and are not short “recovery” laps
 * when duration could collide with rest).
 * @returns {Set<number>|null} lap_index set, or null if plan has no usable work durations
 */
function workLapIndicesByPlannedDurations(exerciseSplits, laps) {
  const workTargets = collectWorkDurationTargets(exerciseSplits);
  if (workTargets.length === 0) return null;

  const restTargets = collectRestDurationTargets(exerciseSplits);
  const workLapIndices = new Set();

  for (const lap of laps) {
    const t = lapMovingTimeSec(lap);
    if (t < 45) continue;

    const distM = parseFloat(lap.distance) || 0;
    const matchesWork = workTargets.some((w) => matchesDurationTarget(t, w));
    if (!matchesWork) continue;

    const matchesRest = restTargets.some((r) => matchesDurationTarget(t, r));
    if (matchesRest && distM > 0 && distM < 450) continue;

    workLapIndices.add(Number(lap.lap_index));
  }

  return workLapIndices.size > 0 ? workLapIndices : null;
}

function segmentOverlap(a0, a1, b0, b1) {
  const lo = Math.max(a0, b0);
  const hi = Math.min(a1, b1);
  return Math.max(0, hi - lo);
}

function aggregateOverLaps(lapRanges, laps, weightForLapIndex) {
  let totalW = 0;
  let paceWeighted = 0;
  let paceW = 0;
  let hrWeighted = 0;
  let hrW = 0;
  let powWeighted = 0;
  let powW = 0;

  for (const lr of lapRanges) {
    const w = weightForLapIndex(lr.lapIndex);
    if (w <= 0) continue;

    const lap = laps.find((l) => Number(l.lap_index) === Number(lr.lapIndex));
    if (!lap) continue;

    totalW += w;

    const speed = parseFloat(lap.average_speed);
    if (Number.isFinite(speed) && speed > 0.05) {
      paceWeighted += (1000 / speed) * w;
      paceW += w;
    }

    const hr = parseFloat(lap.average_heartrate);
    if (Number.isFinite(hr) && hr > 0) {
      hrWeighted += hr * w;
      hrW += w;
    }

    const watts = parseFloat(lap.average_watts);
    if (Number.isFinite(watts) && watts > 0) {
      powWeighted += watts * w;
      powW += w;
    }
  }

  if (totalW <= 0) return null;

  return {
    paceSecPerKm: paceW > 0 ? paceWeighted / paceW : null,
    avgHr: hrW > 0 ? hrWeighted / hrW : null,
    avgWatts: powW > 0 ? powWeighted / powW : null,
    totalWorkOverlapM: totalW,
  };
}

/**
 * Distance overlap between Strava laps and planned work segments (fallback when durations unknown).
 */
function computeByDistanceOverlap(exerciseSplits, activityDistanceM, laps) {
  const splitRanges = buildExerciseSplitRanges(exerciseSplits, activityDistanceM);
  const workIntervals = [];
  for (let i = 0; i < exerciseSplits.length; i++) {
    if (!isWorkIntensitySplit(exerciseSplits[i])) continue;
    const r = splitRanges[i];
    if (!r || r.endM <= r.startM) continue;
    workIntervals.push({ startM: r.startM, endM: r.endM });
  }
  if (workIntervals.length === 0) return null;

  const lapRanges = buildLapRanges(laps);
  return aggregateOverLaps(lapRanges, laps, (lapIndex) => {
    const lr = lapRanges.find((x) => Number(x.lapIndex) === Number(lapIndex));
    if (!lr) return 0;
    let lapOverlap = 0;
    for (const w of workIntervals) {
      lapOverlap += segmentOverlap(lr.startM, lr.endM, w.startM, w.endM);
    }
    return lapOverlap;
  });
}

/** Full-lap distance weights for laps classified as work by planned duration. */
function computeByDurationMatchedLaps(exerciseSplits, laps) {
  const indices = workLapIndicesByPlannedDurations(exerciseSplits, laps);
  if (!indices) return null;

  const lapRanges = buildLapRanges(laps);
  const distByIndex = new Map(lapRanges.map((lr) => [Number(lr.lapIndex), lr.endM - lr.startM]));

  return aggregateOverLaps(lapRanges, laps, (lapIndex) =>
    indices.has(Number(lapIndex)) ? distByIndex.get(Number(lapIndex)) || 0 : 0
  );
}

/**
 * @param {Array<{ lap_index: number, run_intensity_class?: string }>|undefined} lapSegmentLinks
 *        from API (linked user_running_exercise_segments.run_intensity_class per lap).
 */
export function workLapIndicesFromSegmentLinks(lapSegmentLinks) {
  if (!Array.isArray(lapSegmentLinks) || lapSegmentLinks.length === 0) return null;
  const set = new Set();
  for (const row of lapSegmentLinks) {
    if (String(row.run_intensity_class || '').toLowerCase() === 'work') {
      set.add(Number(row.lap_index));
    }
  }
  return set.size > 0 ? set : null;
}

function computeByLapIndexSet(indices, laps) {
  const lapRanges = buildLapRanges(laps);
  const distByIndex = new Map(lapRanges.map((lr) => [Number(lr.lapIndex), lr.endM - lr.startM]));
  return aggregateOverLaps(lapRanges, laps, (lapIndex) =>
    indices.has(Number(lapIndex)) ? distByIndex.get(Number(lapIndex)) || 0 : 0
  );
}

/**
 * Distance-weighted pace (sec/km), HR, and watts over Strava laps that belong to planned work.
 * Prefers matching laps by **moving time vs planned work duration** (avoids route-distance skew from proportional splits).
 * @param {Array<{ lap_index: number, run_intensity_class?: string }>|undefined} lapSegmentLinks
 * @returns {null | { paceSecPerKm: number|null, avgHr: number|null, avgWatts: number|null, totalWorkOverlapM: number }}
 */
export function computeWorkSplitWeightedAverages(
  exerciseSplits,
  activityDistanceM,
  laps,
  lapSegmentLinks
) {
  if (!shouldShowWorkSplitSummary(exerciseSplits)) return null;
  if (!Array.isArray(laps) || laps.length === 0) return null;

  const fromLinks = workLapIndicesFromSegmentLinks(lapSegmentLinks);
  if (fromLinks) {
    const byLinked = computeByLapIndexSet(fromLinks, laps);
    if (byLinked) return byLinked;
  }

  const byDuration = computeByDurationMatchedLaps(exerciseSplits, laps);
  if (byDuration) return byDuration;

  return computeByDistanceOverlap(exerciseSplits, activityDistanceM, laps);
}
